import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Avatar, Card, Skeleton, Tag, Typography } from 'antd';
import { palette } from '../app/theme';

/** Month-over-month change in percent; null when last month was zero but this month is not. */
export function trendPct(current: number, previous: number): number | null {
  if (previous === 0) return current === 0 ? 0 : null;
  return Math.round(((current - previous) / previous) * 1000) / 10;
}

/** Soft green/red pill with a signed percentage, as on the stat cards; `downIsGood` turns the colours round (unsubscribes, bounces). */
export function TrendTag({ pct, downIsGood = false }: { pct: number | null; downIsGood?: boolean }) {
  if (pct === null) return <Tag color={downIsGood ? 'error' : 'success'} style={{ marginInlineEnd: 0, fontWeight: 500 }}>New</Tag>;
  const good = downIsGood ? pct < 0 : pct > 0;
  const color = pct === 0 ? 'default' : good ? 'success' : 'error';
  return (
    <Tag color={color} style={{ marginInlineEnd: 0, fontWeight: 500 }}>
      {pct > 0 ? '+' : ''}
      {pct}%
    </Tag>
  );
}

/** Round soft-blue icon badge used on stat cards and list rows. */
export function IconBadge({ icon, size = 40 }: { icon: ReactNode; size?: number }) {
  return <Avatar size={size} icon={icon} style={{ background: palette.primarySoft, color: palette.primary, flex: 'none' }} />;
}

export interface StatCardProps {
  icon: ReactNode;
  label: string;
  value: ReactNode;
  /** Trend pill plus a short explanation, e.g. "vs last month". */
  trend?: { pct: number | null; text: string; downIsGood?: boolean };
  /** Secondary line shown when there is no trend. */
  note?: ReactNode;
  loading?: boolean;
  to?: string;
  /** A small extra line under the trend or note (e.g. where most of a number came from). */
  detail?: ReactNode;
}

/** Metric card: icon, label, large value, then a trend pill or note. */
export function StatCard({ icon, label, value, trend, note, loading, to, detail }: StatCardProps) {
  const body = (
    <Card hoverable={!!to} style={{ height: '100%' }}>
      <IconBadge icon={icon} />
      <Typography.Text type="secondary" style={{ display: 'block', marginTop: 18, fontSize: 13 }}>
        {label}
      </Typography.Text>
      {loading ? (
        <Skeleton active title={false} paragraph={{ rows: 2, width: ['60%', '80%'] }} style={{ marginTop: 10 }} />
      ) : (
        <>
          <div style={{ fontSize: 26, fontWeight: 600, letterSpacing: '-0.01em', lineHeight: 1.3, marginTop: 4, color: palette.text }}>{value}</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 12, minHeight: 22 }}>
            {trend ? (
              <>
                <TrendTag pct={trend.pct} downIsGood={trend.downIsGood} />
                <Typography.Text type="secondary" style={{ fontSize: 13 }}>{trend.text}</Typography.Text>
              </>
            ) : (
              <Typography.Text type="secondary" style={{ fontSize: 13 }}>{note}</Typography.Text>
            )}
          </div>
          {detail && (
            <Typography.Text type="secondary" style={{ display: 'block', fontSize: 12, marginTop: 8 }}>
              {detail}
            </Typography.Text>
          )}
        </>
      )}
    </Card>
  );
  return to ? <Link to={to} style={{ display: 'block', height: '100%' }}>{body}</Link> : body;
}

/** Two-line card title: heading plus a muted description, as in the reference cards. */
export function CardTitle({ title, subtitle }: { title: ReactNode; subtitle?: ReactNode }) {
  return (
    <div style={{ whiteSpace: 'normal' }}>
      <div style={{ fontSize: 17, fontWeight: 600, color: palette.text }}>{title}</div>
      {subtitle && <div style={{ fontSize: 13, fontWeight: 400, color: palette.textSecondary, marginTop: 4 }}>{subtitle}</div>}
    </div>
  );
}
